/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.ui.scrolling

import kotlinx.collections.immutable.toImmutableList

/**
 * Holds information about tab states. It gets information from
 * [IntersectionObserver].
 *
 * When using [IntersectionObserver] for our app we have to deal with following challenges.
 *
 * It is necessary to make behavior intuitive for user.  So we take first header as decisive.
 * But sections can be to big that they don't fit on the screen. In this case we use text
 * currently displayed as decisive.
 *
 * For more info about behaviour please see unit tests.
 */
class TabStatesRegister(sectionIds: List<String>) {
    val sectionIds = sectionIds.toImmutableList()

    /** Contains header ids for section. */
    private val visibleHeaders = mutableSetOf<String>()

    /** Contains section ids. */
    private val visibleSections = mutableSetOf<String>()

    fun updateIdState(
        elementId: String,
        intersecting: Boolean
    ) {
        if (elementId.isHeaderId()) {
            if (intersecting) {
                visibleHeaders += elementId
            } else {
                visibleHeaders -= elementId
            }
        } else if (elementId.isSectionId()) {
            if (intersecting) {
                visibleSections += elementId
            } else {
                visibleSections -= elementId
            }
        }
    }

    /** Iterates over all tabs and provides information whether
     * tab is selected. Invokes [block] for every tab.
     */
    fun forEachTab(block: (tabId: String, isSelected: Boolean) -> Unit) {
        sectionIds.forEach { sectionId ->
            block(sectionId.toSectionTabId(), isTabSelected(sectionId))
        }
    }

    /**
     * @param sectionId section id
     * @return true if tab with [sectionId] should be displayed as selected
     */
    fun isTabSelected(sectionId: String): Boolean {
        // first we check whether any header is visible
        return if (visibleHeaders.isNotEmpty()) {
            // at least one header is visible
            val firstVisibleSection = sectionIds.find { visibleHeaders.contains(it.toSectionHeaderId()) }
            val headerVisible = firstVisibleSection == sectionId
            headerVisible
        } else {
            // header is not visible, lets search sections
            val firstVisibleSection = sectionIds.find { visibleSections.contains(it) }
            firstVisibleSection == sectionId
        }
    }
}

private const val SUFFIX_TAB = "-tab"
private const val SUFFIX_HEADER = "-header"

private fun String.isHeaderId() = endsWith(SUFFIX_HEADER)

private fun String.isSectionId() = !endsWith(SUFFIX_HEADER)

fun String.toSectionTabId() = this + SUFFIX_TAB

fun String.toSectionHeaderId() = this + SUFFIX_HEADER
